// ============================================================
// A CUT'S CAST CHANGE ON THE DISPLAY (plan design-gaps, Z3; the scenes page):
// each traveller pulses where it landed, a party ring running out; each figure
// the cut brought on gets a ring as it stands up (the stand itself is its
// posture onset); each leaver fades where it stood, a ring and its height.
// From hooks/useCastChange, which lasts a moment. Looks only.
// ============================================================

import { memo } from 'react';
import type { Camera } from '../../engine/battlefield/projection';
import { SCENE_VIEW, project } from '../../engine/battlefield/projection';
import { tokenEye, tokenFoot, type Token } from '../../engine/battlefield/tokens';
import { groundRing } from './BeatMarks';
import styles from '../../styles/Display.module.css';

/** The rings' reach, metres (the scenes page: a traveller's pulse 2.5 m, the cast's ring 1.6 m). */
const PULSE_M = 2.5;
const CAST_M = 1.6;

/** A ring on the floor about a figure, growing from its foot: the foot is the animation's origin. */
function Ring({ token, m, kind, camera }: { token: Token; m: number; kind: string; camera: Camera }) {
  const foot = project(tokenFoot(token), camera, SCENE_VIEW);
  const origin = { '--foot-x': `${foot.x}px`, '--foot-y': `${foot.y}px` } as React.CSSProperties;
  return (
    <polygon data-cast={kind} data-id={token.id} style={origin} points={groundRing(tokenFoot(token), m, camera)} />
  );
}

/** A leaver fading where it stood: its ring and its height. */
function Leaver({ token, camera }: { token: Token; camera: Camera }) {
  const [foot, eye] = [tokenFoot(token), tokenEye(token)].map((p) => project(p, camera, SCENE_VIEW));
  return (
    <g data-cast="leaver" data-id={token.id} className={styles.castLeaver}>
      <polygon points={groundRing(tokenFoot(token), token.footprint, camera)} />
      <line x1={foot.x} y1={foot.y} x2={eye.x} y2={eye.y} />
    </g>
  );
}

export default memo(function CastRings({
  cast,
  tokens,
  camera,
}: {
  cast: { entering: readonly string[]; leavers: readonly Token[]; travellers: readonly Token[] };
  tokens: Readonly<Record<string, Token>>;
  camera: Camera;
}) {
  // Nearly always nothing: say so before building anything.
  if (!cast.entering.length && !cast.leavers.length && !cast.travellers.length) return null;
  const entering = cast.entering.map((id) => tokens[id]).filter((t): t is Token => !!t);
  return (
    <g data-testid="cast-change" className={styles.castChange} pointerEvents="none">
      {cast.travellers.map((t) => (
        <Ring key={t.id} token={tokens[t.id] ?? t} m={PULSE_M} kind="traveller" camera={camera} />
      ))}
      {entering.map((t) => (
        <Ring key={t.id} token={t} m={CAST_M} kind="entering" camera={camera} />
      ))}
      {cast.leavers.map((t) => (
        <Leaver key={t.id} token={t} camera={camera} />
      ))}
    </g>
  );
});
